/* eslint-disable no-nested-ternary,react/no-danger */

import { yupResolver } from '@hookform/resolvers/yup';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  Link,
  Stack,
  Typography,
} from 'src/components/mui';

import { MenuSelect as LanguageSelect } from '../menu-select/MenuSelect';

import { NavItem } from './components/NavItem';

import ThemeConfig from 'src/theme';
import { getAsset } from 'src/utils';
import { FullScreenBackground } from '../fullscreen-background/FullScreenBackground';
import HelpDialog from '../help-dialog';

import { Loader } from '../loader/Loader';
import { ResetContainer } from '../reset-container/ResetContainer';
import TermsDialog from '../terms-dialog';
import { TipsCarousel } from '../tips-carousel/TipsCarousel';
import { TitleAndDescription } from './components/TitleAndDescription';
import Checkbox from './components/checkbox';
import { Input } from './components/input/Input';
import { SCHEMA } from './schema';
import { Props, TInputs } from './types';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { FAIcon } from 'src/components/icons';
import { PlayCircle } from 'lucide-react';

export const IntroScreen = ({
  backgroundImage,
  backgroundVideo,
  loading,
  loadingProgress,
  language,
  termsUrl,
  whiteLabel,
  title,
  description,
  isTextShadowed,
  enableTips,
  darkBackground,
  blurBackground,
  hideHelpButton,
  inputs,
  checkbox,
  translation,
  client,
  onSubmit: onSubmitProp,
  rtl,
  restricted,
  button,
  preventSubmit,
  showPassword,
  isEnabled,
  preventSubmitCallback,
}: Props) => {
  const [showHelp, setShowHelp] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [revealPassword, setRevealPassword] = useState(false);
  const [parent] = useAutoAnimate();

  const form = useForm<TInputs>({
    defaultValues: {
      name: '',
      password: '',
      phone: '',
      email: '',
      legal1: false,
      legal2: false,
    },
    resolver: yupResolver(SCHEMA),
    context: { inputs, checkbox, showPassword },
  });

  const { handleSubmit, setValue } = form;

  const onSubmit: SubmitHandler<TInputs> = (data, e) => {
    if (preventSubmit) {
      e?.preventDefault();
      preventSubmitCallback?.();
      return;
    }
    const { password, name, phone, email, legal1, legal2 } = data;

    if (showPassword) {
      e?.preventDefault();
      onSubmitProp({
        password,
      });

      if (!isEnabled) {
        setSubmitted(true);
      }

      setValue('name', '');
    } else {
      onSubmitProp({
        name,
        phone,
        email,
        legalBookingBox: legal1,
        legalBookingBox2: legal2,
      });
      setSubmitted(true);
    }
  };

  return (
    <>
      <ThemeConfig
        primaryColor={whiteLabel.isWLUser ? whiteLabel.primaryColor : undefined}
        secondaryColor={whiteLabel.isWLUser ? whiteLabel.secondaryColor : undefined}
        textColor={whiteLabel.isWLUser ? whiteLabel.textColor : undefined}
      >
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ResetContainer id="intro-screen" sx={{ direction: rtl ? 'rtl' : 'initial' }}>
              <>
                {backgroundVideo ? (
                  <FullScreenBackground
                    blurBackground={blurBackground}
                    url={backgroundVideo}
                    type="video"
                  />
                ) : (
                  <FullScreenBackground blurBackground={blurBackground} url={backgroundImage} />
                )}
                <Box
                  sx={{
                    zIndex: 1,
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    paddingY: 2,
                    overflow: 'hidden',
                    background: darkBackground ? 'rgba(0, 0, 0, .5)' : 'none',
                  }}
                >
                  {client.show && (
                    <Avatar
                      sx={{
                        ml: 1,
                        position: 'absolute',
                        top: 10,
                        width: client.horizontalLogo ? '200px' : '100px',
                        maxWidth: '20vw',
                        height: 'auto',
                        '&>img': {
                          objectFit: 'contain',
                        },
                      }}
                      component={Link}
                      src={client.logo}
                      href={client.website}
                      target="_blank"
                      variant="rounded"
                    />
                  )}
                  <Stack
                    px={2}
                    direction="row"
                    justifyContent={{ xs: 'center', sm: 'flex-end' }}
                    alignItems="center"
                    position={{ xs: 'absolute', sm: 'relative' }}
                    bottom={{ xs: 10, sm: 0 }}
                    width="100%"
                  >
                    <Stack direction="row" alignItems="center">
                      {language && language?.options?.length > 1 && (
                        <LanguageSelect
                          containerSx={{ mr: 2 }}
                          value={language.default}
                          placeholder={translation?.language || 'Language'}
                          items={language.options}
                          onChange={language.onChange}
                        />
                      )}

                      {termsUrl && (
                        <NavItem onClick={() => setShowTerms(true)} sx={{ color: 'white' }}>
                          {translation?.terms || 'Terms'}
                        </NavItem>
                      )}
                      {!hideHelpButton && (
                        <NavItem
                          onClick={() => setShowHelp(true)}
                          variant="text"
                          sx={{
                            color: 'white',
                            '&:hover': {
                              color: 'white',
                            },
                          }}
                        >
                          {translation?.help || 'Help'}
                        </NavItem>
                      )}
                      {whiteLabel.show && (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{ display: { xs: 'none', sm: 'block' }, color: 'white' }}
                          >
                            {translation?.poweredBy || 'Powered by'}
                          </Typography>
                          <Avatar
                            sx={{
                              width: { xs: '90px', sm: '130px' },
                              '&>img': {
                                objectFit: 'contain',
                              },
                            }}
                            component={Link}
                            src={whiteLabel.logo || getAsset('logo-white.svg')}
                            href={whiteLabel.website}
                            target="_blank"
                            variant="rounded"
                          />
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                  <Container
                    maxWidth="md"
                    sx={{ height: '100%', maxHeight: '100%', overflowY: 'auto', mt: 2, pb: 2 }}
                  >
                    <Stack
                      pt={2}
                      pb={4}
                      justifyContent="space-around"
                      minHeight="100%"
                      spacing={2}
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      <TitleAndDescription
                        title={title}
                        description={description}
                        isTextShadowed={isTextShadowed}
                      />
                      <Stack ref={parent} spacing={2} mt={1} mb={2}>
                        {showPassword ? (
                          <Stack justifyContent="center" alignItems="center" spacing={4}>
                            <Input
                              rtl={rtl}
                              name="password"
                              type={!revealPassword ? 'password' : 'text'}
                              label={translation?.password || 'Password'}
                              sx={{
                                '.MuiInputBase-root': {
                                  color: 'white',
                                  '.MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'white !important',
                                  },
                                },
                                maxWidth: '332px',
                                margin: 'auto',
                                width: '100%',
                              }}
                              InputProps={{
                                endAdornment: (
                                  <IconButton
                                    onClick={() => setRevealPassword((prev) => !prev)}
                                    sx={{ color: 'white' }}
                                  >
                                    <i
                                      className={revealPassword ? 'fas fa-eye' : 'fas fa-eye-slash'}
                                    />
                                  </IconButton>
                                ),
                              }}
                            />

                            <Button
                              fullWidth
                              variant="contained"
                              size="large"
                              type="submit"
                              sx={{
                                maxWidth: '332px',
                                mx: 'auto',
                                color: button?.secondaryColor,
                                backgroundColor: button?.primaryColor,
                                '&:hover': {
                                  backgroundColor: button?.primaryColor
                                    ? alpha(button.primaryColor, 0.8)
                                    : undefined,
                                },
                              }}
                            >
                              {translation?.login || 'Login'}
                            </Button>
                          </Stack>
                        ) : (
                          <>
                            <Stack
                              direction={{ xs: 'column', md: 'row' }}
                              width="100%"
                              maxWidth={{ xs: '332px', md: '100%' }}
                              marginX="auto"
                              spacing={2}
                              sx={{
                                justifyContent: 'center',
                                alignItems: {
                                  xs: 'center',
                                  md: 'normal',
                                },
                              }}
                            >
                              {inputs.name && (
                                <Input
                                  rtl={rtl}
                                  name="name"
                                  label={translation?.name || 'Name'}
                                  sx={{
                                    '.MuiInputBase-root': {
                                      color: 'white',
                                      '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white !important',
                                      },
                                    },
                                    // If it's just the name input, make it the same width as the button
                                    ...(!inputs.email && !inputs.phone
                                      ? { maxWidth: '332px', margin: 'auto', width: '100%' }
                                      : {}),
                                  }}
                                />
                              )}
                              {inputs.phone && (
                                <Input
                                  rtl={rtl}
                                  type="tel"
                                  name="phone"
                                  label={translation?.phone || 'Phone'}
                                  sx={{
                                    '.form-control': {
                                      color: 'white',
                                      boxShadow: 'white',
                                      borderColor: 'white !important',
                                    },
                                  }}
                                />
                              )}
                              {inputs.email && (
                                <Input
                                  rtl={rtl}
                                  name="email"
                                  label={translation?.email || 'Email'}
                                  sx={{
                                    '.MuiInputBase-root': {
                                      color: 'white',
                                      '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'white !important',
                                      },
                                    },
                                  }}
                                />
                              )}
                            </Stack>
                            <Stack spacing={1} textAlign="left">
                              {checkbox.legal1 && (
                                <Checkbox
                                  rtl={rtl}
                                  name="legal1"
                                  label={checkbox.legal1}
                                  overflowWrap="anywhere"
                                  sx={{
                                    color: 'white !important',
                                    '.Mui-checked': {
                                      color: 'white !important',
                                    },
                                  }}
                                />
                              )}
                              {checkbox.legal2 && (
                                <Checkbox
                                  rtl={rtl}
                                  name="legal2"
                                  label={checkbox.legal2}
                                  overflowWrap="anywhere"
                                  sx={{
                                    color: 'white !important',
                                    '.Mui-checked': {
                                      color: 'white !important',
                                    },
                                  }}
                                />
                              )}
                            </Stack>

                            <Stack direction="row" sx={{ width: '100%', justifyContent: 'center' }}>
                              {loading && (
                                <Loader
                                  sx={{
                                    marginTop: '-4px',
                                    marginBottom: '-20px',
                                    position: 'relative',
                                  }}
                                  value={loadingProgress}
                                />
                              )}
                              {!submitted && (
                                <>
                                  {button?.showPlayButton ? (
                                    <IconButton
                                      type={restricted ? 'button' : 'submit'}
                                      sx={{
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        width: '70px',
                                        height: '70px',
                                        color: button?.primaryColor,
                                        '&:hover': {
                                          color: button?.primaryColor
                                            ? alpha(button.primaryColor, 0.8)
                                            : undefined,
                                        },
                                      }}
                                    >
                                      <FAIcon icon={PlayCircle} size={70} />
                                    </IconButton>
                                  ) : (
                                    <Button
                                      ref={parent}
                                      fullWidth
                                      variant="contained"
                                      size="large"
                                      type="submit"
                                      sx={{
                                        maxWidth: '332px',
                                        mx: 'auto',
                                        color: button?.secondaryColor,
                                        backgroundColor: button?.primaryColor,
                                        '&:hover': {
                                          backgroundColor: button?.primaryColor
                                            ? alpha(button.primaryColor, 0.8)
                                            : undefined,
                                        },
                                      }}
                                    >
                                      {button?.label ||
                                        translation?.introScreenButtonLabel ||
                                        'Start'}
                                    </Button>
                                  )}
                                </>
                              )}
                            </Stack>
                          </>
                        )}
                      </Stack>

                      {enableTips ? <TipsCarousel translation={translation} /> : <Box />}
                    </Stack>
                  </Container>
                </Box>
              </>
            </ResetContainer>
          </form>
        </FormProvider>
      </ThemeConfig>
      {showHelp && (
        <HelpDialog open={showHelp} onClose={() => setShowHelp(false)} translation={translation} />
      )}
      {showTerms && termsUrl && (
        <TermsDialog
          logo={whiteLabel?.logo}
          url={termsUrl}
          open={showTerms}
          onClose={() => setShowTerms(false)}
        />
      )}
    </>
  );
};
